// frontend/src/components/ThemeSwitcher.tsx
// Floating palette button (bottom-right) that slides open a panel for
// picking a color theme.
// Animated with framer-motion. Accessible via keyboard.

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Tooltip from '@mui/material/Tooltip';
import PaletteIcon from '@mui/icons-material/Palette';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';

import { useShieldTheme } from '../theme/ThemeContext';
import { ALL_THEMES, ThemeId } from '../theme/tokens';

export default function ThemeSwitcher() {
  const { themeId, tokens, setTheme } = useShieldTheme();
  const [open, setOpen] = useState(false);

  const t = tokens;

  return (
    <>
      {/* ── Floating trigger button ────────────────────────────────────── */}
      <Tooltip title="Appearance" placement="left">
        <motion.button
          onClick={() => setOpen(true)}
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Open appearance settings"
          style={{
            position:       'fixed',
            bottom:         '28px',
            right:          '24px',
            zIndex:         9998,
            width:          '48px',
            height:         '48px',
            borderRadius:   t.radiusFull,
            background:     t.gradientButton,
            border:         'none',
            cursor:         'pointer',
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            boxShadow:      t.shadowGlow,
            color:          t.textOnPrimary,
          }}
        >
          <PaletteIcon fontSize="small" />
        </motion.button>
      </Tooltip>

      {/* ── Panel + backdrop ──────────────────────────────────────────── */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              onClick={() => setOpen(false)}
              style={{
                position:       'fixed',
                inset:          0,
                background:     'rgba(0,0,0,0.45)',
                zIndex:         9998,
                backdropFilter: 'blur(2px)',
              }}
            />

            {/* Slide-in panel */}
            <motion.div
              key="panel"
              initial={{ x: 340, opacity: 0 }}
              animate={{ x: 0,   opacity: 1 }}
              exit={{   x: 340, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 320, damping: 30 }}
              style={{
                position:       'fixed',
                top:            0,
                right:          0,
                bottom:         0,
                width:          '320px',
                zIndex:         9999,
                background:     t.bgElevated,
                borderLeft:     `1px solid ${t.borderDefault}`,
                display:        'flex',
                flexDirection:  'column',
                overflow:       'hidden',
              }}
            >
              {/* ── Panel header ──────────────────────────────────────── */}
              <div style={{
                padding:        '20px 20px 16px',
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'space-between',
                borderBottom:   `1px solid ${t.borderSubtle}`,
              }}>
                <div>
                  <div style={{
                    fontFamily: t.fontDisplay,
                    fontSize:   '16px',
                    fontWeight: 700,
                    color:      t.textPrimary,
                  }}>
                    Appearance
                  </div>
                  <div style={{ fontSize: '12px', color: t.textMuted, marginTop: '2px' }}>
                    Choose your color theme
                  </div>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Close appearance panel"
                  style={{
                    background:     'none',
                    border:         'none',
                    cursor:         'pointer',
                    color:          t.textMuted,
                    padding:        '4px',
                    borderRadius:   t.radiusSm,
                    display:        'flex',
                    alignItems:     'center',
                    justifyContent: 'center',
                  }}
                >
                  <CloseIcon fontSize="small" />
                </button>
              </div>

              {/* ── Panel body ────────────────────────────────────────── */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px 24px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {Object.values(ALL_THEMES).map(th => {
                    const active = th.id === themeId;
                    return (
                      <motion.button
                        key={th.id}
                        onClick={() => setTheme(th.id as ThemeId)}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.98 }}
                        style={{
                          width:        '100%',
                          background:   active ? t.primaryMuted : t.bgSurface,
                          border:       `2px solid ${active ? t.primary : t.borderSubtle}`,
                          borderRadius: t.radiusLg,
                          cursor:       'pointer',
                          padding:      '14px 16px',
                          display:      'flex',
                          alignItems:   'center',
                          gap:          '14px',
                          transition:   'all 0.2s',
                          textAlign:    'left',
                          boxShadow:    active ? t.shadowGlow : t.shadowCard,
                        }}
                      >
                        {/* Color swatches */}
                        <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                          <div style={{
                            width:        28,
                            height:       28,
                            borderRadius: t.radiusMd,
                            background:   th.bgCanvas,
                            border:       `1px solid ${th.borderDefault}`,
                          }} />
                          <div style={{
                            width:        28,
                            height:       28,
                            borderRadius: t.radiusMd,
                            background:   th.primary,
                          }} />
                          <div style={{
                            width:        28,
                            height:       28,
                            borderRadius: t.radiusMd,
                            background:   th.accent,
                          }} />
                        </div>

                        {/* Labels */}
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontFamily: t.fontBody,
                            fontWeight: 700,
                            fontSize:   '14px',
                            color:      t.textPrimary,
                          }}>
                            {th.name}
                          </div>
                          <div style={{
                            fontSize:  '12px',
                            color:     t.textMuted,
                            marginTop: '2px',
                          }}>
                            {th.description}
                          </div>
                        </div>

                        {/* Active check */}
                        {active && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            style={{
                              width:          20,
                              height:         20,
                              borderRadius:   t.radiusFull,
                              background:     t.primary,
                              display:        'flex',
                              alignItems:     'center',
                              justifyContent: 'center',
                              flexShrink:     0,
                            }}
                          >
                            <CheckIcon style={{ fontSize: 12, color: t.textOnPrimary }} />
                          </motion.div>
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              {/* ── Panel footer ──────────────────────────────────────── */}
              <div style={{
                padding:   '12px 20px 16px',
                borderTop: `1px solid ${t.borderSubtle}`,
                fontSize:  '11px',
                color:     t.textMuted,
                textAlign: 'center',
              }}>
                Preferences saved automatically
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
